import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import { match, P } from "ts-pattern";
import { Page } from "../../../activities/AppPage";
import { API } from "../../../API";
import { useSocket } from "../../../hooks/useSocket";
import { AgencyId, CommCenterGetTicketResponse, CommCenterTicket } from "../../../messages";
import { STRINGS } from "../../../strings";
import { AppCaregiver } from "../../../system/AppState/AppCaregiverState";
import { translate } from "../../../translate";
import { QueryStatus } from "../../../utils/reactQueryUtils";
import { withMedflytAgencyId } from "../../withMedflytAgencyId";
import { chatRoomQueryKey } from "../ChatRoom/chatRoomQueryKey";
import { sortTicketsByLastMessage } from "../utils/chatUtils";
import ChatsMainScreen from "./ChatsMainScreen";

interface Props {
  api: API;
  caregiver: AppCaregiver;
  medflytAgencyId: AgencyId;
  onRequestNavigate: (page: Page) => void;
  onClickChatWithOtherAgencies: () => void;
}

export const chatTicketsQueryKey = ["chat_tickets"];

function ChatMainActivity(props: Props) {
  const commCenterTicketsQuery = useQuery({
    queryKey: chatTicketsQueryKey,
    queryFn: () => props.api.getChatTickets(),
  });

  const handleNavigateToTicket = React.useCallback(
    (ticket: CommCenterTicket) =>
      props.onRequestNavigate.call(null, { type: "ChatRoom", ticketId: ticket.id }),
    [props.onRequestNavigate]
  );

  const commCenterNewTicketMutation = useMutation({
    mutationFn: () =>
      props.api.createCommCenterTicket({
        agencyId: props.medflytAgencyId,
        source: "MOBILE_CHAT",
      }),
    onSuccess: (data) => {
      queryClient.setQueryData<CommCenterGetTicketResponse>(chatRoomQueryKey(data.ticket.id), data);

      handleNavigateToTicket(data.ticket);
    },
  });

  const queryClient = useQueryClient();

  useSocket({
    eventName: "CommCenterTicketUpdated",
    onEvent: (payload) => {
      console.log("[ChatMainActivity] CommCenterTicketUpdated");
      commCenterTicketsQuery.refetch();
      queryClient.invalidateQueries(chatRoomQueryKey(payload.ticketId));
    },
  });

  return match(commCenterTicketsQuery)
    .with(QueryStatus.Error, () => <p>{translate(STRINGS.SOMETHING_WENT_WRONG)}</p>)
    .with(P.union(QueryStatus.Loading, QueryStatus.Idle), () => (
      <ChatsMainScreen
        isLoading={true}
        caregiverFirstName={props.caregiver.firstName}
        onClickChatWithOtherAgencies={props.onClickChatWithOtherAgencies}
      />
    ))
    .with(QueryStatus.ResolvedAs("data"), (result) => {
      return (
        <ChatsMainScreen
          isLoading={false}
          caregiverFirstName={props.caregiver.firstName}
          agencyId={props.medflytAgencyId}
          tickets={result.data.tickets.sort(sortTicketsByLastMessage)}
          isCreatingNewTicket={commCenterNewTicketMutation.isLoading}
          canChatWithOtherAgencies={props.caregiver.canChatWithOtherAgencies}
          onClickChatWithOtherAgencies={props.onClickChatWithOtherAgencies}
          onTicketClick={handleNavigateToTicket}
          onClickNewTicket={commCenterNewTicketMutation.mutate}
        />
      );
    })
    .exhaustive();
}

export default withMedflytAgencyId(ChatMainActivity);
